import React, { useEffect, useState, useRef } from "react";
import {
  ArrowLeft,
  Loader2,
  Save,
  CheckCircle2,
  AlertCircle,
  ImagePlus,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import {
  crearCampania,
  actualizarCampania,
  obtenerCampaniaPorId,
  subirLogoCampania,
  subirImagenCampania,
} from "../services/campaniaService.ts";

export default function CampaniaCrear() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();

  const logoInputRef = useRef<HTMLInputElement>(null);
  const imagenInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(isEdit);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: "ok" | "error"; texto: string } | null>(null);

  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    fecha_inicio: "",
    fecha_fin: "",
    estado: "borrador",
    descuento: "",
    terminos: "",
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [imagenFile, setImagenFile] = useState<File | null>(null);
  const [imagenPreview, setImagenPreview] = useState<string | null>(null);

  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [existingImagenUrl, setExistingImagenUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
      if (imagenPreview?.startsWith("blob:")) URL.revokeObjectURL(imagenPreview);
    };
  }, [logoPreview, imagenPreview]);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/campanias");
      return;
    }
    if (isEdit && id) {
      loadCampania(Number(id));
    }
  }, [isAdmin, id]);

  const loadCampania = async (campaniaId: number) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const campania = await obtenerCampaniaPorId(campaniaId, token);
      setForm({
        nombre: campania.nombre || "",
        descripcion: campania.descripcion || "",
        fecha_inicio: campania.fecha_inicio || "",
        fecha_fin: campania.fecha_fin || "",
        estado: campania.estado || "borrador",
        descuento: campania.descuento || "",
        terminos: campania.terminos || "",
      });
      if (campania.logo_url) setExistingLogoUrl(campania.logo_url);
      if (campania.imagen_url) setExistingImagenUrl(campania.imagen_url);
    } catch (e) {
      setMsg({ tipo: "error", texto: "Error al cargar la campaña" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(selected);
    setLogoPreview(URL.createObjectURL(selected));
  };

  const handleImagenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (imagenPreview?.startsWith("blob:")) URL.revokeObjectURL(imagenPreview);
    setImagenFile(selected);
    setImagenPreview(URL.createObjectURL(selected));
  };

  const handleClearLogo = () => {
    if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setExistingLogoUrl(null);
    if (logoInputRef.current) logoInputRef.current.value = "";
  };

  const handleClearImagen = () => {
    if (imagenPreview?.startsWith("blob:")) URL.revokeObjectURL(imagenPreview);
    setImagenFile(null);
    setImagenPreview(null);
    setExistingImagenUrl(null);
    if (imagenInputRef.current) imagenInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMsg({ tipo: "error", texto: "El nombre es requerido" });
      return;
    }
    if (!form.fecha_inicio || !form.fecha_fin) {
      setMsg({ tipo: "error", texto: "Las fechas son requeridas" });
      return;
    }

    setGuardando(true);
    setMsg(null);
    try {
      const token = await getAccessTokenSilently();
      let campaniaId: number;

      if (isEdit && id) {
        await actualizarCampania(Number(id), form, token);
        campaniaId = Number(id);
        setMsg({ tipo: "ok", texto: "Campaña actualizada correctamente" });
      } else {
        const result = await crearCampania(form, token);
        campaniaId = result.campania_id;
        setMsg({ tipo: "ok", texto: "Campaña creada correctamente" });
      }

      if (logoFile) {
        const logoUrl = await subirLogoCampania(campaniaId, logoFile, getAccessTokenSilently);
        const freshLogo = `${logoUrl}${logoUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        setExistingLogoUrl(freshLogo);
        setLogoFile(null);
        if (logoPreview?.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
        setLogoPreview(null);
      }

      if (imagenFile) {
        const imagenUrl = await subirImagenCampania(campaniaId, imagenFile, getAccessTokenSilently);
        const freshImg = `${imagenUrl}${imagenUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        setExistingImagenUrl(freshImg);
        setImagenFile(null);
        if (imagenPreview?.startsWith("blob:")) URL.revokeObjectURL(imagenPreview);
        setImagenPreview(null);
      }

      if (!isEdit) {
        setTimeout(() => navigate("/campanias-admin"), 1200);
      }
    } catch (e: any) {
      const texto = e?.response?.data?.message || "Error al guardar la campaña";
      setMsg({ tipo: "error", texto });
    } finally {
      setGuardando(false);
    }
  };

  const logoMostrado = logoPreview || existingLogoUrl;
  const imagenMostrada = imagenPreview || existingImagenUrl;

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/campanias-admin")}
          className="mb-6 flex items-center gap-1 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Volver a gestión
        </button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? "Editar Campaña" : "Nueva Campaña"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {isEdit ? "Modificá los datos de la campaña" : "Completá los datos para crear una nueva campaña"}
          </p>
        </div>

        {msg && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              msg.tipo === "ok"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
          >
            {msg.tipo === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            {msg.texto}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Información General</h2>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange("nombre", e.target.value)}
                    placeholder="Ej: Black Friday 2026"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange("descripcion", e.target.value)}
                    placeholder="Describí la campaña..."
                    rows={3}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Descuento</label>
                  <input
                    type="text"
                    value={form.descuento}
                    onChange={(e) => handleChange("descuento", e.target.value)}
                    placeholder="Ej: 20% OFF"
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Fechas y Estado</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Fecha inicio *</label>
                  <input
                    type="date"
                    value={form.fecha_inicio}
                    onChange={(e) => handleChange("fecha_inicio", e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Fecha fin *</label>
                  <input
                    type="date"
                    value={form.fecha_fin}
                    onChange={(e) => handleChange("fecha_fin", e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-slate-700">Estado</label>
                <select
                  value={form.estado}
                  onChange={(e) => handleChange("estado", e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="borrador">Borrador</option>
                  <option value="activa">Activa</option>
                  <option value="finalizada">Finalizada</option>
                  <option value="cancelada">Cancelada</option>
                </select>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Multimedia</h2>

              <div className="space-y-6">
                {/* Logo */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <ImagePlus className="h-4 w-4 text-slate-400" />
                      Logo de la campaña
                    </span>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 file:mr-4 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-blue-700"
                    />
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    Formatos sugeridos: PNG o JPG. Se recomienda imagen cuadrada.
                  </p>
                  {logoMostrado && (
                    <div className="mt-3 inline-flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                      <img
                        src={logoMostrado}
                        alt="Logo de campaña"
                        className="h-16 w-16 rounded-lg border border-slate-200 object-contain bg-white"
                      />
                      <div className="text-xs text-slate-600">
                        <p className="font-semibold text-slate-700">
                          {logoFile ? "Logo nuevo seleccionado" : "Logo actual"}
                        </p>
                        <p>{logoFile?.name || "Imagen guardada en la campaña"}</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearLogo}
                        className="rounded-lg p-1 text-slate-400 transition hover:bg-rose-100 hover:text-rose-600"
                        title="Quitar logo"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Imagen principal */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <ImagePlus className="h-4 w-4 text-slate-400" />
                      Imagen principal de la campaña
                    </span>
                    <input
                      ref={imagenInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImagenChange}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 file:mr-4 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-blue-700"
                    />
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    Imagen que se mostrará como banner de la campaña. Formato JPG o PNG.
                  </p>
                  {imagenMostrada && (
                    <div className="mt-3">
                      <div className="relative inline-flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                        <img
                          src={imagenMostrada}
                          alt="Imagen de campaña"
                          className="h-24 w-40 rounded-lg border border-slate-200 object-cover"
                        />
                        <div className="text-xs text-slate-600">
                          <p className="font-semibold text-slate-700">
                            {imagenFile ? "Imagen nueva seleccionada" : "Imagen actual"}
                          </p>
                          <p>{imagenFile?.name || "Imagen guardada en la campaña"}</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearImagen}
                          className="rounded-lg p-1 text-slate-400 transition hover:bg-rose-100 hover:text-rose-600"
                          title="Quitar imagen"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">Términos y Condiciones</h2>
              <textarea
                value={form.terminos}
                onChange={(e) => handleChange("terminos", e.target.value)}
                placeholder="Escribí los términos y condiciones de la campaña..."
                rows={5}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={guardando}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700 disabled:opacity-50"
              >
                {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEdit ? "Guardar cambios" : "Crear campaña"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/campanias-admin")}
                className="rounded-xl border border-slate-200 px-6 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
